import { Workshift } from '@mv-submodules/inplant-plantanalysis-fe-iblu/types/workshift'

import moment from 'moment'
import InfluxDataFetcher from './_InfluxDataFetcher'
import type { InfluxMeasureSeries, InfluxSeriesMeasuresData } from './_types'
import { flattenTimeSeriesData, normalizeTimeSeriesData } from './_utils'
import { ParsedSerieDatum, parseInfluxResponse } from './_influxResponseParser'
import { WordSlug } from './measures-utils'

/**
 * Configuration for fetching a standard measure from Influx.
 *
 * @property {string} assetId - Unique identifier of the asset
 * @property {string} measureId - Unique identifier of the measure to fetch
 * @property {string} measureSlug - Short name/identifier used as a key in the result data
 * @property {'standard'} type - Type identifier for standard measures
 */
interface StandardMeasureToFetch {
  assetId: string
  measureId: string
  measureSlug: string
  type: 'standard'
  /**
   * If true, the data will be aggregated.
   * If not specified, the default value is false.
   */
  aggregate?: boolean
  /**
   * The time window in minutes for aggregation.
   * If not specified, the default value is 1 minute.
   */
  aggregateByMinutes?: number
  /**
   * The name of the influx table column to use as value.
   * If not specified, the default value is 'measure'.
   */
  valueColumnName?: string
}

/**
 * Configuration for fetching a calculated measure from Influx (excluding summary type).
 *
 * @property {'single_column_state_serie'|'multi_column_state_serie'} type - Type of calculated measure
 * @property {string} measureSlug - Short name/identifier used as a key in the result data
 * @property {string} measureId - Unique identifier of the calculated measure
 */
interface CalculatedMeasureToFetchNonSummary {
  type: 'single_column_state_serie' | 'multi_column_state_serie'
  measureSlug: string
  measureId: string
}

/**
 * Configuration for fetching a calculated measure from Influx.
 *
 * @property {'summary'} type - Type identifier for summary measures
 * @property {string} measureSlug - Short name/identifier used as a key in the result data
 * @property {string} measureId - Unique identifier of the calculated measure
 */
interface CalculatedMeasureToFetchForSummaryData {
  type:  'summary'
  measureSlug: string
  measureId: string
}

/**
 * Configuration for fetching a word from Influx.
 *
 * @property {string} wordId - Unique identifier of the word
 * @property {string} wordSlug - Short name/identifier for the word type (MDIR, INV, etc.)
 */
interface WordToFetch {
  type: 'word'
  assetId: string
  wordId: string
  wordSlug: WordSlug
}

/**
 * Union type representing all measure types that can be fetched for time series data.
 * 
 * This type excludes summary measures which are handled separately and are used for 
 * widgets that need detailed time-series data points rather than aggregated summaries.
 */
type MeasureToFetchForSeries = StandardMeasureToFetch | CalculatedMeasureToFetchNonSummary | WordToFetch

/**
 * Union type representing measures that can be fetched for summary data aggregation.
 */
type MeasureToFetchForSummary = CalculatedMeasureToFetchForSummaryData

/**
 * Configuration options for fetching chart series data from Influx.
 *
 * @property {MeasureToFetchForSeries[]} measuresToFetch - List of measures to fetch data for (excludes summary measures)
 * @property {string} plant - Plant identifier
 * @property {string} date - Date string for the request
 * @property {Workshift} workshift - Workshift object containing start and end times
 */
interface PropsForSeries {
  measuresToFetch: MeasureToFetchForSeries[]
  plant: string
  date: string
  workshift: Workshift
}

/**
 * Configuration options for fetching chart data from Influx.
 *
 * @property {MeasureToFetchForSummary[]} measuresToFetch - List of summary measures to fetch data for
 * @property {string} plant - Plant identifier
 * @property {string} date - Date string for the request
 * @property {Workshift} workshift - Workshift object containing start and end times
 */
interface PropsForSummaryData {
  measuresToFetch: MeasureToFetchForSummary[]
  plant: string
  date: string
  workshift: Workshift
}

interface Options {
  abortController?: AbortController
  includeLastValueFromPreviousShift?: boolean
}

type GetInfluxRawSeriesMeasuresDataResult = Array<{
    measureSlug: string;
    data: ParsedSerieDatum[];
    type: "standard";
  } | {
    measureSlug: WordSlug;
    data: Record<string, ParsedSerieDatum[]>;
    type: "word";
  } | {
    measureSlug: string;
    data: ParsedSerieDatum[];
    type: "single_column_state_serie";
  } | {
    measureSlug: string;
    data: ParsedSerieDatum[];
    type: "multi_column_state_serie";
  }>

/**
 * Fetches raw time series data from Influx for a set of measures.
 *
 * This function calls Influx for each requested measure (standard, calculated or word),
 * parses the responses into a normalized internal structure, and returns them grouped
 * by `measureSlug`. It does **not** align timestamps or flatten the result – that is
 * handled by `getChartInfluxSeriesData`.
 *
 * @param {PropsForSeries} props - Configuration options for the data fetch
 * @param {MeasureToFetchForSeries[]} props.measuresToFetch - Array of measures to fetch data for (excludes summary measures)
 * @param {string} props.plant - Plant identifier
 * @param {string} props.date - Date string for the data request
 * @param {Workshift} props.workshift - Workshift object containing start and end times
 * @param {Options} options - Additional options including optional AbortController
 *
 * @returns {Promise<GetInfluxMeasuresDataResult>} Promise resolving to an array of objects,
 *          each containing `measureSlug`, parsed `data` and the measure `type`.
 */
const _getInfluxRawSeriesMeasuresData = async (props: PropsForSeries, options: Options = {}): Promise<GetInfluxRawSeriesMeasuresDataResult> => {
  const influxDataFetcher = new InfluxDataFetcher(options)

  return Promise.all(
    props.measuresToFetch.map(async measure => {
      if (measure.type === 'standard') {
        const response = await influxDataFetcher.requestMeasure({
          assetId: measure.assetId,
          measureId: measure.measureId,
          workshift: props.workshift,
          date: props.date,
          plant: props.plant,
          aggregate: measure.aggregate,
          aggregateByMinutes: measure.aggregateByMinutes,
          valueColumnName: measure.valueColumnName,
        })
        return {
          measureSlug: measure.measureSlug,
          data: parseInfluxResponse(response.data, { measureType: 'standard' }),
          type: measure.type,
        }
      } else if (measure.type === 'word') {
        const response = await influxDataFetcher.requestMeasure({
          assetId: measure.assetId,
          measureId: measure.wordId,
          workshift: props.workshift,
          date: props.date,
          plant: props.plant,
        })
        return {
          measureSlug: measure.wordSlug,
          data: parseInfluxResponse(response.data, { measureType: 'word', wordSlug: measure.wordSlug }),
          type: measure.type,
        }
      } else {
        const response = await influxDataFetcher.requestCalculatedMeasure({
          measureId: measure.measureId,
          workshift: props.workshift, 
          plant: props.plant,
        })
        if (measure.type === 'single_column_state_serie') {
          return {
            measureSlug: measure.measureSlug,
            data: parseInfluxResponse(response.data, { measureType: 'single_column_state_serie' }),
            type: measure.type,
          }
        }
        return {
          measureSlug: measure.measureSlug,
          data: parseInfluxResponse(response.data, { measureType: 'multi_column_state_serie' }),
          type: measure.type,
        }
      }
    })
  )
}

/**
 * Fetches and processes time series data from Influx for chart visualization.
 *
 * This function retrieves data for multiple measures (standard, calculated or word),
 * normalizes the timestamps across all datasets to the selected workshift window,
 * and flattens them into a structure suitable for charts and visualizations.
 *
 * @param {PropsForSeries} props - Configuration options for the data fetch
 * @param {MeasureToFetchForSeries[]} props.measuresToFetch - Array of measures to fetch data for (excludes summary measures)
 * @param {string} props.plant - Plant identifier
 * @param {string} props.date - Date string for the data request
 * @param {Workshift} props.workshift - Workshift object containing start and end times
 * @param {Options} options - Additional options including optional AbortController
 *
 * @returns {Promise<InfluxSeriesMeasuresData>} Promise resolving to flattened time series data where each object
 *          contains a timestamp and values for each requested measure.
 *
 * @example
 * const data = await getNormalizedInfluxTimeSeriesData({
 *   measuresToFetch: [
 *     { type: 'standard', assetId: 'asset1', measureId: 'measure1', measureSlug: 'temp' },
 *     { type: 'single_column_state_serie', measureId: 'calc1', measureSlug: 'efficiency' }
 *   ],
 *   plant: 'plant1',
 *   date: '2023-04-01',
 *   workshift: { start: '2023-04-01T08:00:00', end: '2023-04-01T16:00:00' }
 * });
 */
const getInfluxSeriesMeasuresData = async (props: PropsForSeries, options: Options = {}): Promise<InfluxSeriesMeasuresData> => {
  const workShiftStart = moment(props.workshift.start).unix()
  const workShiftEnd = moment(props.workshift.end).unix()

  const measuresData = await _getInfluxRawSeriesMeasuresData(props, options)

  const resultToObject = measuresData.reduce((acc, result) => {
    if (Array.isArray(result.data)) {
      acc[result.measureSlug] = result.data
    } else if (typeof result.data === 'object') {
      Object.entries(result.data).forEach(([key, value]) => {
        acc[key] = value
      })
    }
    return acc
  }, {} as Record<string, InfluxMeasureSeries>)

  const normalizedData = normalizeTimeSeriesData(resultToObject, workShiftStart, workShiftEnd)

  return flattenTimeSeriesData(normalizedData)
}

type GetInfluxSummaryMeasuresDataResult = Array<{
  measureSlug: string;
  data: Record<string, number>;
  type: "summary";
}>

/**
 * Low-level helper that fetches raw aggregated summary data from Influx.
 *
 * This function calls Influx for each requested summary measure and parses the
 * responses into a typed internal structure. It **does not** normalize or reshape
 * the result; that is done by `getNormalizedInfluxSummaryMeasuresData`.
 *
 * @param {PropsForSummaryData} props - Configuration options for the data fetch
 * @param {MeasureToFetchForSummary[]} props.measuresToFetch - Array of summary measures to fetch data for
 * @param {string} props.plant - Plant identifier
 * @param {string} props.date - Date string for the data request
 * @param {Workshift} props.workshift - Workshift object containing start and end times
 * @param {Options} options - Additional options including optional AbortController
 *
 * @returns {Promise<GetInfluxSummaryMeasuresDataResult>} Promise resolving to an array of
 *          `{ measureSlug, data, type }` objects.
 */
const _getInfluxRawSummaryMeasuresData = async (
  props: PropsForSummaryData,
  options: Options = {}
): Promise<GetInfluxSummaryMeasuresDataResult> => {
  const influxDataFetcher = new InfluxDataFetcher(options)

  return Promise.all(
    props.measuresToFetch.map(async measure => {
        const response = await influxDataFetcher.requestSummaryMeasure({
          measureId: measure.measureId,
          workshift: props.workshift,
          plant: props.plant,
          date: props.date,
        })
        return {
          measureSlug: measure.measureSlug,
          data: parseInfluxResponse(response.data, { measureType: 'summary' }),
          type: measure.type,
        }
    })
  )
}

type InfluxSummaryMeasuresData = Record<string, Record<string, number>>

/**
 * Fetches aggregated summary data from Influx and normalizes it into a map.
 *
 * This is the high-level API used by widgets that need summary statistics rather than
 * detailed time-series points. It wraps `_getInfluxRawSummaryMeasuresData` and converts
 * the array of results into an object indexed by `measureSlug`.
 *
 * @param {PropsForSummaryData} props - Configuration options for the data fetch
 * @param {MeasureToFetchForSummary[]} props.measuresToFetch - Array of summary measures to fetch data for
 * @param {string} props.plant - Plant identifier
 * @param {string} props.date - Date string for the data request
 * @param {Workshift} props.workshift - Workshift object containing start and end times
 * @param {Options} options - Additional options including optional AbortController
 *
 * @returns {Promise<Record<string, Record<string, number>>>} Promise resolving to an
 *          object shaped as `{ [measureSlug]: { [fieldName]: value } }`.
 *
 * @example
 * ```typescript
 * const summaryData = await getChartInfluxSummaryData({
 *   measuresToFetch: [
 *     { type: 'summary', measureId: 'vDecSpeedTorque', measureSlug: 'decSpeedTorque' }
 *   ],
 *   plant: 'PLANT_001',
 *   date: '2024-01-15',
 *   workshift: { start: '06:00', end: '14:00' }
 * })
 * // summaryData.decSpeedTorque dec_f019_torque, dec_f019_speed, ...
 * ```
 */
const getInfluxSummaryMeasuresData = async (props: PropsForSummaryData, options: Options = {}): Promise<InfluxSummaryMeasuresData> => {
  const results = await _getInfluxRawSummaryMeasuresData(props, options)
  return results.reduce((acc, result) => {
    if (result.data) {
      acc[result.measureSlug] = result.data
    }
    return acc
  }, {} as InfluxSummaryMeasuresData)
}

export { getInfluxSeriesMeasuresData, getInfluxSummaryMeasuresData }
export type { MeasureToFetchForSeries, MeasureToFetchForSummary, InfluxSeriesMeasuresData, InfluxSummaryMeasuresData }
